// Thin fetch wrappers — cookie-based auth means we just rely on the browser.

export class ApiError extends Error {
  constructor(public status: number, public payload: unknown, message: string) {
    super(message);
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body ? { "content-type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try { parsed = JSON.parse(text); }
    catch { parsed = text; }
  }
  if (!res.ok) {
    const msg = (parsed as { message?: string; error?: string })?.message
      ?? (parsed as { error?: string })?.error
      ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, parsed, msg);
  }
  return parsed as T;
}

export const api = {
  get:    <T = unknown>(path: string)               => call<T>("GET", path),
  post:   <T = unknown>(path: string, body?: unknown) => call<T>("POST", path, body),
  put:    <T = unknown>(path: string, body?: unknown) => call<T>("PUT", path, body),
  patch:  <T = unknown>(path: string, body?: unknown) => call<T>("PATCH", path, body),
  del:    <T = unknown>(path: string)               => call<T>("DELETE", path),
  delete: <T = unknown>(path: string)               => call<T>("DELETE", path),
};

// ─── Types ─────────────────────────────────────────────

export interface User { id: string; email: string; }
export interface Workspace {
  id: string; slug: string; name: string; plan: string; role?: string;
}
export interface Release {
  id: string; workspace_id: string; slug: string; title: string; artist: string;
  genre: string | null; bpm: number | null; musical_key: string | null;
  release_date: string | null; catalog_no: string | null;
  description: string | null; stage: string; created_at: string;
  artwork_url: string | null;
}
export interface Smartlink {
  id: string; release_id: string; slug: string; destinations_json: string;
  pixel_ids_json: string | null; created_at: string; url?: string;
}
export interface DashboardSummary {
  workspace: Workspace;
  usage: {
    plan: { id: string; name: string; releasesPerMonth: number | null; fans: number | null; djs: number | null };
    releases: { used: number; limit: number | null };
    fans: { used: number; limit: number | null };
    djs: { used: number; limit: number | null };
  };
  counts: { releases: number; fans: number; djs: number };
  recentReleases: Release[];
  recentEvents: Array<{ id: number; type: string; release_id: string | null; ts: string }>;
}
export interface ReleaseTrack {
  track_id: string;
  title: string;
  artist: string | null;
  bpm: number | null;
  musical_key: string | null;
  duration_seconds: number | null;
  track_number: number | null;
  asset_id: string | null;
  analysis_status: string | null;
  upload_status: string | null;
  artwork_url: string | null;
}
export interface ReleaseDetail {
  release: Release;
  counts: Record<string, number>;
  destinations: Record<string, number>;
  countries: Array<{ country: string; n: number }>;
  smartlinks: Smartlink[];
  smartlinkUrl: string | null;
  platformStats: Array<{ platform: string; external_url: string; plays: number | null; likes: number | null; reposts: number | null; comments: number | null; fetched_at: string }>;
  platformBaseline: Array<{ platform: string; external_url: string; plays: number | null; fetched_at: string }>;
  djSignalCount: number;
  tracks: ReleaseTrack[];
}
export interface StatsHistoryResponse {
  byPlatform: Record<string, Array<{ ts: string; plays: number | null; likes: number | null }>>;
}
export interface PlatformCommentRow {
  id: string;
  release_id: string;
  platform: string;
  external_id: string;
  author: string | null;
  body: string | null;
  is_dj_signal: number;
  posted_at: string | null;
}
export interface CommentsResponse {
  signals: PlatformCommentRow[];
  recent: PlatformCommentRow[];
}
export interface FetchOutcome {
  release: string;
  platform: string;
  url: string;
  ok: boolean;
  plays?: number;
  likes?: number;
  reposts?: number;
  comments?: number;
  message?: string;
}
export interface FansResponse {
  total: number;
  byCountry: Array<{ country: string; n: number }>;
  recent: Array<{ id: string; email: string | null; country: string | null; source: string | null; first_seen: string }>;
}
export interface FanTimelineEvent {
  id: number;
  ts: string;
  type: string;
  destination: string | null;
  country: string | null;
  release: { slug: string; title: string; artist: string } | null;
  via_dj: string | null;
}
export interface FanDetailResponse {
  fan: {
    id: string; email: string | null; country: string | null; source: string | null;
    fingerprint: string | null; first_seen: string; last_seen: string;
  };
  timeline: FanTimelineEvent[];
  firstTouchDj: { id: string; name: string } | null;
  byRelease: Array<{
    release: { slug: string; title: string; artist: string };
    counts: Record<string, number>;
  }>;
}
export interface BillingResponse {
  currentPlan: { id: string; name: string; monthlyPriceEUR: number | null; features: string[] };
  allPlans: Array<{ id: string; name: string; monthlyPriceEUR: number | null; features: string[]; releasesPerMonth: number | null; fans: number | null; djs: number | null }>;
  usage: DashboardSummary["usage"];
  stripe: { configured: boolean };
}
export interface DJ {
  id: string;
  name: string;
  email: string | null;
  scene: string | null;
  tier: number;
  genres: string | null;
  bpm_min: number | null;
  bpm_max: number | null;
  notes: string | null;
  play_rate: number;
  fan_conversion: number;
  last_promoed_at: string | null;
}
export interface DJPick {
  dj_id: string;
  score: number;
  reason: string;
  /** True when the DJ has fewer than 3 downloads — sorted last in the picks
   *  array, score is 0, UI should render an "unranked" badge instead. */
  unranked: boolean;
  dj: DJ;
}
export interface FanSegment {
  name: string;
  size_estimate: number;
  criteria: string;
  angle: string;
}
export type ChatRole = "user" | "assistant";
export interface ChatMessage { role: ChatRole; content: string; }

export interface IntegrationFieldDef { key: string; label: string; hint?: string; }
export interface IntegrationSecretFieldDef extends IntegrationFieldDef { isSet: boolean; }
export interface IntegrationCard {
  provider: string;
  label: string;
  description: string;
  publicFields: IntegrationFieldDef[];
  secretFields: IntegrationSecretFieldDef[];
  config: Record<string, string>;
  enabled: boolean;
  hasSecrets: boolean;
  updated_at: string | null;
}
export interface IntegrationSaveResponse {
  provider: string;
  config: Record<string, string>;
  enabled: boolean;
  hasSecrets: boolean;
  updated_at: string;
}

// ─── Endpoints ─────────────────────────────────────────

// Wire shape of nox-api's auth responses (merchants, not workspaces). Local
// transforms below preserve the SPA's "workspaces" vocabulary so AuthProvider
// and pages don't need to know about the rename.
interface NoxMerchant {
  id: string;
  slug: string | null;
  name: string;
  status?: string;
  role: string;
  default_currency?: string;
  default_region?: string;
}
function merchantToWorkspace(m: NoxMerchant): Workspace {
  return {
    id: m.id,
    slug: m.slug ?? m.id,            // fallback to id if slug not set yet
    name: m.name,
    plan: "",                         // populated by /summary or /billing call later
    role: m.role,
  };
}

export const endpoints = {
  me: async (): Promise<{ user: User; workspaces: Workspace[] }> => {
    const raw = await api.get<{ user: User; merchants: NoxMerchant[] }>("/api/auth/me");
    return { user: raw.user, workspaces: raw.merchants.map(merchantToWorkspace) };
  },
  signup: async (b: { email: string; password: string; workspaceName?: string }): Promise<{ user: User; workspace: Workspace }> => {
    const raw = await api.post<{ user: User; merchant: NoxMerchant; token: string }>("/api/auth/signup", {
      email: b.email,
      password: b.password,
      merchant_name: b.workspaceName ?? b.email.split("@")[0],
    });
    return { user: raw.user, workspace: merchantToWorkspace(raw.merchant) };
  },
  login: async (b: { email: string; password: string }): Promise<{ user: User; workspaces: Workspace[] }> => {
    const raw = await api.post<{ user: User; merchants: NoxMerchant[]; token: string }>("/api/auth/login", b);
    return { user: raw.user, workspaces: raw.merchants.map(merchantToWorkspace) };
  },
  logout: () => api.post("/api/auth/logout"),
  summary: (ws: string)          => api.get<DashboardSummary>(`/api/w/${ws}/summary`),
  listReleases: (ws: string)     => api.get<Release[]>(`/api/w/${ws}/releases`),
  createRelease: (ws: string, b: { title: string; artist: string; genre?: string; bpm?: number; musical_key?: string; release_date?: string; description?: string }) =>
    api.post<Release>(`/api/w/${ws}/releases`, b),
  getRelease: (ws: string, slug: string) => api.get<ReleaseDetail>(`/api/w/${ws}/releases/${slug}`),
  createSmartlink: (ws: string, slug: string, destinations: { platform: string; url: string }[]) =>
    api.post<Smartlink>(`/api/w/${ws}/releases/${slug}/smartlink`, { destinations }),
  uploadInit: (ws: string, files: Array<{ filename: string; contentType: string; bytes: number }>) =>
    api.post<{ uploads: Array<{ trackId: string; assetId: string; bucket: string; objectPath: string; signedUploadUrl: string }> }>(
      `/api/w/${ws}/uploads/init`, { files }
    ),
  uploadComplete: (ws: string, assetIds: string[]) =>
    api.post<{ ok: true }>(`/api/w/${ws}/uploads/complete`, { assetIds }),
  uploadStatus: (ws: string, assetIds: string[]) =>
    api.get<{
      assets: Record<string, {
        status: "queued" | "processing" | "retrying" | "completed" | "failed" | "dead" | "unknown";
        queuePosition: number | null;
        estimatedWaitSeconds: number | null;
        attempt: number;
        maxAttempts: number;
        errorCode: string | null;
        errorMessage: string | null;
        processingElapsedSeconds: number | null;
        trackTitle: string | null;
      }>;
      avgProcessingSeconds: number;
    }>(`/api/w/${ws}/uploads/status?assetIds=${encodeURIComponent(assetIds.join(","))}`),
  attachTracks: (ws: string, releaseId: string, trackIds: string[]) =>
    api.post<{ release_id: string; tracks: Array<{ trackId: string; trackNumber: number | null }> }>(
      `/api/w/${ws}/releases/${releaseId}/tracks/attach`, { trackIds }
    ),
  fetchStats: (ws: string, slug: string) =>
    api.post<{ outcomes: FetchOutcome[] }>(`/api/w/${ws}/releases/${slug}/stats/fetch`),
  statsHistory: (ws: string, slug: string) =>
    api.get<StatsHistoryResponse>(`/api/w/${ws}/releases/${slug}/stats/history`),
  comments: (ws: string, slug: string) =>
    api.get<CommentsResponse>(`/api/w/${ws}/releases/${slug}/comments`),
  djPick: (ws: string, slug: string) =>
    api.post<{ picks: DJPick[] }>(`/api/w/${ws}/releases/${slug}/promo/dj-pick`),
  agentMessage: (ws: string, messages: ChatMessage[]) =>
    api.post<{ reply: string }>(`/api/w/${ws}/agent/message`, { messages }),
  fanSegments: (ws: string)      => api.post<{ segments: FanSegment[] }>(`/api/w/${ws}/fans/segment`),
  fans: (ws: string)             => api.get<FansResponse>(`/api/w/${ws}/fans`),
  getFan: (ws: string, id: string) => api.get<FanDetailResponse>(`/api/w/${ws}/fans/${id}`),
  billing: (ws: string)          => api.get<BillingResponse>(`/api/w/${ws}/billing`),
  upgrade: (ws: string, plan: string) =>
    api.post<{ ok: boolean; mocked?: boolean; message?: string }>(`/api/w/${ws}/billing/checkout`, { plan }),
  integrations: (ws: string) =>
    api.get<IntegrationCard[]>(`/api/w/${ws}/integrations`),
  saveIntegration: (ws: string, provider: string, body: { config?: Record<string, string>; secrets?: Record<string, string | null>; enabled?: boolean }) =>
    api.put<IntegrationSaveResponse>(`/api/w/${ws}/integrations/${provider}`, body),
  removeIntegration: (ws: string, provider: string) =>
    api.del<{ ok: true }>(`/api/w/${ws}/integrations/${provider}`),
};
